import React, { useEffect, useState } from "react";
import { Grid, Switch, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useDispatch } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import axios from "axios";
import { useMaterialUIController } from "context";

const AppVersionSettings = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [loading, setLoading] = useState(false);
  const [minAppVersion, setMinAppVersion] = useState("");
  const [updateUrl, setUpdateUrl] = useState("");
  const [isMandatory, setIsMandatory] = useState(true);
  const [description, setDescription] = useState("");
  const dispatch = useDispatch();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_APII}/app-version`);
      if (response.data.success) {
        setMinAppVersion(response.data.minAppVersion);
        setUpdateUrl(response.data.updateUrl);
        setIsMandatory(response.data.isMandatory ?? true);
        setDescription(response.data.description || "");
      }
    } catch (error) {
      console.error("Error fetching app version:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdate = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APII}/app-version`,
        {
          minAppVersion,
          updateUrl,
          isMandatory,
          description,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      dispatch(
        handleAlert({
          isOpen: true,
          type: response?.data?.success ? "success" : "error",
          msg: response?.data?.message,
        })
      );
    } catch (error) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: "Failed to update app version",
        })
      );
    }
  };

  return (
    <MDBox py={3}>
      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress color="info" />
        </MDBox>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>
            <MDBox
              sx={({ palette: { dark, white } }) => ({
                border: 1,
                borderColor: darkMode ? white.main : dark.main,
                borderRadius: 2,
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              })}
            >
              <MDTypography variant="h5" fontWeight="medium">
                Mobile App Version Settings
              </MDTypography>

              <MDBox>
                <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                  Minimum App Version (e.g. 6.4.3)
                </MDTypography>
                <MDInput
                  fullWidth
                  value={minAppVersion}
                  onChange={(e) => setMinAppVersion(e.target.value)}
                  placeholder="Ex: 6.4.3"
                  sx={{ mt: 1 }}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                  Play Store Update URL
                </MDTypography>
                <MDInput
                  fullWidth
                  value={updateUrl}
                  onChange={(e) => setUpdateUrl(e.target.value)}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  sx={{ mt: 1 }}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                  Update Description
                </MDTypography>
                <MDInput
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's new in this version?"
                  sx={{ mt: 1 }}
                />
              </MDBox>

              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDBox>
                  <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Force Update (Mandatory)
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    If enabled, users will be forced to update to the latest version.
                  </MDTypography>
                </MDBox>
                <Switch
                  checked={isMandatory}
                  onChange={(e) => setIsMandatory(e.target.checked)}
                  color="info"
                />
              </MDBox>

              <MDBox display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={handleUpdate}>
                  Save Settings
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      )}
    </MDBox>
  );
};

export default AppVersionSettings;
